// Client-safe helpers for "next salah" from /api/prayer payloads.
// Accepts both 24h ("18:24") and 12h ("6:24 PM") time strings.

export const PRAYER_ORDER = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'] as const
export type PrayerName = (typeof PRAYER_ORDER)[number]
export type RawPrayerTimes = Partial<Record<PrayerName, string>>

export function toMin(t: string): number | null {
  const m = t.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?/i)
  if (!m) return null
  let h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (m[3]) h = (h % 12) + (m[3].toUpperCase() === 'PM' ? 12 : 0)
  return h * 60 + min
}

export function fmt12(mins: number): string {
  const h24 = Math.floor(mins / 60) % 24
  const m = mins % 60
  const ampm = h24 >= 12 ? 'PM' : 'AM'
  const h = h24 % 12 || 12
  return `${h}:${String(m).padStart(2, '0')} ${ampm}`
}

/** Next upcoming prayer for the given raw times, e.g. { name: 'MAGHRIB', time: '6:42 PM' } */
export function nextPrayerFrom(raw: RawPrayerTimes): { name: string; time: string } | null {
  const sched = PRAYER_ORDER
    .map((name) => ({ name, min: toMin(raw[name] || '') }))
    .filter((p): p is { name: PrayerName; min: number } => p.min !== null)
  if (sched.length < 5) return null
  const now = new Date()
  const nowMin = now.getHours() * 60 + now.getMinutes()
  const next = sched.find((p) => p.min > nowMin) || sched[0]
  return { name: next.name.toUpperCase(), time: fmt12(next.min) }
}
